import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import PageHeader from "@/components/feature/PageHeader";
import ArticleBody from "@/pages/topic-detail/components/ArticleBody";
import ArticleAside from "@/pages/topic-detail/components/ArticleAside";
import { topics } from "@/mocks/topics";
import { formatDate, formatHeat } from "@/utils/format";

export default function TopicDetail() {
  const { id } = useParams<{ id: string }>();
  const topic = topics.find((item) => item.id === id);

  if (!topic) {
    return (
      <div className="mx-auto flex w-full flex-col items-center px-4 py-40 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
          <i className="ri-emotion-unhappy-line" aria-hidden="true"></i>
        </span>
        <h1 className="mt-6 text-2xl text-foreground-950 md:text-3xl">这个专题已经下线了</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          它可能被合并进了新的合集。回到热梗库看看当下正在热传的内容吧。
        </p>
        <Link
          to="/topics"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm text-background-50 hover:bg-primary-600"
        >
          返回热梗库
        </Link>
      </div>
    );
  }

  return <TopicDetailView topic={topic} />;
}

function TopicDetailView({ topic }: { topic: (typeof topics)[number] }) {
  const related = topics.filter((item) => topic.relatedIds.includes(item.id));

  useSeo({
    title: `${topic.title} - 513吃瓜`,
    description: topic.summary,
    keywords: topic.tags.join(","),
    path: `/topics/${topic.id}`,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: topic.title,
        description: topic.summary,
        image: topic.cover,
        datePublished: topic.date,
        dateModified: topic.date,
        inLanguage: "zh-CN",
        author: { "@type": "Organization", name: topic.author },
        publisher: { "@type": "Organization", name: "513吃瓜" },
        mainEntityOfPage: `https://law818.cn/topics/${topic.id}`,
        keywords: topic.tags.join(","),
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://law818.cn/" },
          { "@type": "ListItem", position: 2, name: "热梗库", item: "https://law818.cn/topics" },
          {
            "@type": "ListItem",
            position: 3,
            name: topic.title,
            item: `https://law818.cn/topics/${topic.id}`,
          },
        ],
      },
    ],
  });

  return (
    <>
      <PageHeader
        eyebrow={topic.categoryName}
        title={topic.title}
        desc={topic.summary}
        crumbs={[{ label: "首页", path: "/" }, { label: "热梗库", path: "/topics" }, { label: topic.categoryName, path: `/topics?category=${topic.category}` }, { label: "专题详情" }]}
      >
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-600">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" aria-hidden="true"></i>
            {topic.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-calendar-line" aria-hidden="true"></i>
            {formatDate(topic.date)}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line" aria-hidden="true"></i>
            约 {topic.readTime} 分钟读完
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-fire-fill text-primary-500" aria-hidden="true"></i>
            热度 {formatHeat(topic.heat)}
          </span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {topic.tags.map((tag) => (
            <span
              key={tag}
              className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1 text-xs text-foreground-600"
            >
              #{tag}
            </span>
          ))}
        </div>
      </PageHeader>

      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-[minmax(0,1fr)_320px]">
        <ArticleBody topic={topic} />
        <ArticleAside topic={topic} related={related} />
      </div>
    </>
  );
}